export const MAX_CLOUD_PAGES = 20000
export const MAX_PAGE_BYTES = 32 * 1024 * 1024
export const VERIFY_BATCH_SIZE = 64
export const UPLOAD_SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000
export const UPLOAD_WIDTH_HEADER = 'X-Tanko-Width'
export const UPLOAD_HEIGHT_HEADER = 'X-Tanko-Height'
/** 換封面時縮圖的來源頁碼；分片上傳的頁碼在路徑裡，這個 header 只給換封面用。 */
export const UPLOAD_PAGE_HEADER = 'X-Tanko-Page'
export const MAX_CLOCK_AHEAD_MS = 5 * 60 * 1000
/** 目錄註冊一次至多 100 本：失敗重試的成本有界，往返次數又不隨作品數線性成長。 */
export const CATALOG_REGISTER_BATCH_SIZE = 100
/** 註冊的 body 上限。100 本在標題與作者都到達長度上限時約 110 KB，256 KiB 留有餘裕。 */
export const CATALOG_REGISTER_BODY_BYTES = 256 * 1024
/**
 * 批次動作一次至多處理幾本。
 *
 * 與目錄註冊同一個量級：請求主體隨作品數線性成長，D1 的單次 batch 也有其限制，明確
 * 拒絕比讓它在某個規模悄悄失敗好。超過的部分由呼叫端切成數段依序送出。
 */
export const BULK_ACTION_BATCH_SIZE = 100
/** 批次動作的 body 上限。100 本的改名在標題到達長度上限時約 55 KB，128 KiB 留有餘裕。 */
export const BULK_ACTION_BODY_BYTES = 128 * 1024
/** 作品在雲端目錄的同步狀態：pending 為已註冊但尚未備份，ready 為圖片已完整備份。 */
export type CloudSyncState = 'pending' | 'ready'
/**
 * 註冊一本作品所需的全部資料，全部取自本地既有索引，不需要讀取任何圖片。
 *
 * `addedAt` 是這本加入本地書庫的時間，用來當雲端目錄項的建立時間。雲端的排序因此與
 * 本地一致；沒有它的話整批註冊會共用同一個「現在」，時間排序等於失效。
 */
export interface CloudCatalogEntry {
  id: string
  title: string
  author: string
  pageCount: number
  coverPage: number
  addedAt: number
}
export type UploadStatus = 'uploading' | 'verifying' | 'verified' | 'ready' | 'cancelled' | 'expired'
/** image 是每頁的全尺寸圖；cover 是整本唯一的封面縮圖分片，page 記來源頁碼。 */
export type UploadKind = 'image' | 'cover'
export interface CloudMutation { updatedAt: number; mutationId: string }
export interface CloudProgress extends CloudMutation { page: number; offset: number }
export interface CloudBookmark extends CloudProgress { id: string; label: string; createdAt: number; deleted: boolean }

export class CloudValidationError extends Error {
  readonly code = 'INVALID_INPUT'
  constructor(message = '資料格式或範圍不正確') { super(message) }
}
export function cloudInteger(value: unknown, minimum: number, maximum: number): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < minimum || value > maximum) throw new CloudValidationError()
  return value
}
export function cloudPageCount(value: unknown): number { return cloudInteger(value, 1, MAX_CLOUD_PAGES) }
export function cloudPage(value: unknown, pageCount: number): number { return cloudInteger(value, 0, cloudPageCount(pageCount) - 1) }
export function cloudOffset(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1) throw new CloudValidationError()
  return value
}
export function cloudText(value: unknown, maximum: number, allowEmpty = false): string {
  if (typeof value !== 'string' || value.includes('\0')) throw new CloudValidationError()
  const text = value.trim()
  if ((!allowEmpty && !text) || [...text].length > maximum) throw new CloudValidationError()
  return text
}
/**
 * 選填的布林旗標。未帶時回傳 undefined，讓呼叫端分得出「沒有指定」與「明確設為 false」
 * ——更名分類時沒帶隱私就不該把它順手關掉。
 */
export function cloudFlag(value: unknown): boolean | undefined {
  if (value === undefined || value === null) return undefined
  if (typeof value !== 'boolean') throw new CloudValidationError()
  return value
}
export function cloudUuid(value: unknown): string {
  if (typeof value !== 'string' || !/^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(value)) throw new CloudValidationError()
  return value.toLowerCase()
}
export function cloudMutation(value: unknown, serverTime: number): CloudMutation {
  if (!value || typeof value !== 'object') throw new CloudValidationError()
  const input = value as Record<string, unknown>
  return { updatedAt: cloudInteger(input.updatedAt, 0, serverTime + MAX_CLOCK_AHEAD_MS), mutationId: cloudUuid(input.mutationId) }
}
/** 以 session 的 serverTime 與請求往返中點估算時差，不修改本機時鐘。 */
export function estimateClockOffset(serverTime: unknown, requestStartedAt: number, responseReceivedAt: number): number {
  const server = cloudInteger(serverTime, 0, Number.MAX_SAFE_INTEGER)
  const start = cloudInteger(requestStartedAt, 0, Number.MAX_SAFE_INTEGER)
  const end = cloudInteger(responseReceivedAt, start, Number.MAX_SAFE_INTEGER)
  return server - (start + (end - start) / 2)
}
/** 在建立 mutation 時固定此時間；重試同一 mutation 不得重新校時。 */
export function correctedMutationTime(localTimestamp: number, correction: number): number {
  cloudInteger(localTimestamp, 0, Number.MAX_SAFE_INTEGER)
  if (!Number.isFinite(correction)) throw new CloudValidationError()
  return cloudInteger(Math.round(localTimestamp + correction), 0, Number.MAX_SAFE_INTEGER)
}
export function isNewerMutation(incoming: CloudMutation, current: CloudMutation | null): boolean {
  return current === null || incoming.updatedAt > current.updatedAt ||
    (incoming.updatedAt === current.updatedAt && incoming.mutationId > current.mutationId)
}
